/**
 * Decide se uma rota deve ser visivel/acessivel para o usuario atual.
 *
 * Respeita o rollout por-implementacao (``userDetails.uses_new_permissions``):
 *
 *  - Implementacao no sistema NOVO E a rota declara permissao
 *    (``requiredPermission`` ou ``requiredAnyPermission``):
 *      -> gate por atomo (``userDetails.permissions``).
 *  - Caso contrario (impl legada, OU rota sem atomo declarado — ex.: rotas
 *    internas DEV/IMG como /groups, /indicators, collapse Internal):
 *      -> gate legado por ``allowedRoles`` (``userDetails.user_role``).
 *
 * Importante: ``requiredFeatures`` e ``hidden`` continuam sendo tratados
 * separadamente por quem chama (Sidebar/Main) — este helper cuida apenas do
 * controle por role/permissao.
 */
export function canAccessRoute(route, userDetails) {
  if (!userDetails) return false;

  const useNew =
    userDetails.uses_new_permissions &&
    (route.requiredPermission || route.requiredAnyPermission);

  if (useNew) {
    const perms = userDetails.permissions || [];
    if (route.requiredAnyPermission) {
      return route.requiredAnyPermission.some((p) => perms.includes(p));
    }
    return perms.includes(route.requiredPermission);
  }

  // Fallback legado (impls fora da allowlist ou rotas internas sem atomo).
  if (route.allowedRoles) {
    return route.allowedRoles.includes(userDetails.user_role);
  }
  return true;
}

/**
 * Gate hibrido para uma ACAO/capacidade (botao, item de menu de acao).
 *
 * Quando a impl usa o sistema novo, decide pelo atomo ``permission``; senao,
 * pelos ``legacyRoles`` (comportamento legado). Mesmo padrao do
 * ``canUseAIChat``, generalizado.
 */
export function canDo(userDetails, permission, legacyRoles) {
  if (!userDetails) return false;
  if (userDetails.uses_new_permissions) {
    return (userDetails.permissions || []).includes(permission);
  }
  return (legacyRoles || []).includes(userDetails.user_role);
}

/**
 * Caminho de aterrissagem pos-login.
 *
 * No sistema NOVO escolhe pela primeira pagina que o usuario pode ver
 * (overview -> results -> inputs -> governance); sem nenhuma -> no-permission.
 * No legado mantem o comportamento por role (CIF/CIP -> inputs, NON ->
 * no-permission, demais -> overview).
 */
export function defaultLandingPath(userDetails) {
  if (!userDetails) return "/main/overview";

  if (userDetails.uses_new_permissions) {
    const perms = userDetails.permissions || [];
    if (perms.length === 0) return "/main/no-permission";
    if (perms.includes("overview.view")) return "/main/overview";
    if (perms.includes("results.view")) return "/main/pl-explorer";
    if (perms.includes("modeling.inputs.view")) return "/main/inputs";
    if (perms.includes("governance.view")) return "/main/initiatives";
    return "/main/no-permission";
  }

  if (userDetails.user_role === "CIF" || userDetails.user_role === "CIP") {
    return "/main/inputs";
  }
  if (userDetails.user_role === "NON") {
    return "/main/no-permission";
  }
  return "/main/overview";
}

export default canAccessRoute;
